/**
 * Custom date string filter to replace moment.js filter
 * Formats ISO date strings to various display formats
 */

// Helper functions to pad numbers
const pad = (num) => String(num).padStart(2, '0');

// Month and day names
const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const monthNamesShort = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];
const dayNames = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
];
const dayNamesShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Get ordinal suffix for day (1st, 2nd, 3rd, etc.)
const getOrdinal = (day) => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

const dateStringFilter = (value, format = 'YYYY-MM-DD') => {
  if (!value) return '';
  
  try {
    // Parse the date string
    const date = new Date(value);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return value; // Return original value if invalid
    }
    
    // Get date components
    const year = date.getFullYear();
    const month = date.getMonth(); // 0-indexed
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();
    
    // Replace format tokens (order matters - replace longer tokens first)
    let formatted = format;
    
    // Year
    formatted = formatted.replace(/YYYY/g, year);
    formatted = formatted.replace(/YY/g, String(year).slice(-2));
    
    // Month
    formatted = formatted.replace(/MMMM/g, monthNames[month]);
    formatted = formatted.replace(/MMM/g, monthNamesShort[month]);
    formatted = formatted.replace(/MM/g, pad(month + 1));
    // Single M (without lookbehind for browser compatibility)
    formatted = formatted.split('M').map((part, i, arr) => {
      if (i === 0) return part;
      if (arr[i-1].endsWith('M')) return 'M' + part;
      return (month + 1) + part;
    }).join('');
    
    // Day
    formatted = formatted.replace(/Do/g, day + getOrdinal(day));
    formatted = formatted.replace(/DD/g, pad(day));
    // Single D
    formatted = formatted.split('D').map((part, i, arr) => {
      if (i === 0) return part;
      if (arr[i-1].endsWith('D') || part.startsWith('o')) return 'D' + part;
      return day + part;
    }).join('');
    
    // Day of week
    formatted = formatted.replace(/dddd/g, dayNames[date.getDay()]);
    formatted = formatted.replace(/ddd/g, dayNamesShort[date.getDay()]);
    
    // Hours
    formatted = formatted.replace(/HH/g, pad(hours));
    formatted = formatted.split('H').map((part, i, arr) => {
      if (i === 0) return part;
      if (arr[i-1].endsWith('H')) return 'H' + part;
      return hours + part;
    }).join('');
    
    // Minutes
    formatted = formatted.replace(/mm/g, pad(minutes));
    formatted = formatted.split('m').map((part, i, arr) => {
      if (i === 0) return part;
      if (arr[i-1].endsWith('m')) return 'm' + part;
      return minutes + part;
    }).join('');
    
    // Seconds
    formatted = formatted.replace(/ss/g, pad(seconds));
    formatted = formatted.split('s').map((part, i, arr) => {
      if (i === 0) return part;
      if (arr[i-1].endsWith('s')) return 's' + part;
      return seconds + part;
    }).join('');
    
    return formatted;
  } catch (error) {
    console.error('dateStringFilter error:', error);
    return value; // Return original value on error
  }
};

// Export both the filter function and the plugin
export { dateStringFilter };

export default {
  install(Vue) {
    Vue.filter('datestring', dateStringFilter);
  }
};
